// @ts-strict-ignore
import { DefaultProjectHelper } from './default-project.helper';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { MountingRailHelper } from './mounting-rail.helper';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { TerminalBlock, TerminalBlockManager } from '../manager/terminal-block.manager';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { ArticleMoveManager, MoveResult } from '../components/article/manager/article-move.manager';
import { ArrayUtils } from '@shared/util/array-utils';
import { ReplaceLabelingArticleHelper } from '../components/label/replace-labeling-helper/replace-labeling-article.helper';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ReplaceLabelingUtils } from '../components/label/replace-labeling-helper/replace-labeling-utils';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { UserModel } from '@user/model/user.model';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ProjectModel } from '../model/project.model';
import { Injectable } from '@angular/core';
import { ProjectModelService } from '../service/project-model.service';
import { ProjectArticleService } from '../service/project-article.service';
import { ProjectMetaElementService } from '../manager/project-meta-element.service';
import { IsProjectType } from '../model/is-project-type';
import { IResponsibleForProjectTypeHelper } from './i-project.helper';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { Busbar } from '@article/helper/replace/busbar/replace-busbar-data';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { InsertArticleUtils } from '../components/article/util/insert-article-utils';
import { ReplaceBusbarService } from '@article/helper/replace/busbar/replace-busbar.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ReplaceDynamicArticleService } from '@article/helper/replace/replace-dynamic-article.service';
import { ProjectType } from '../model/project-type';
import { SequenceOperation } from '@shared/undo/operation/sequence-operation';

@Injectable({ providedIn: 'root' })
export class RailProjectHelper extends DefaultProjectHelper implements IResponsibleForProjectTypeHelper {
  constructor(
    userModel: UserModel,
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    undoRedoHelper: UndoRedoHelper,
    alertHelper: AlertHelper,
    compositeArticleUtils: CompositeArticleUtils,
    containerService: ContainerService,
    labelingUtils: LabelingUtils,
    articleContainerUtils: ArticleContainerUtils,
    private mountingRailHelper: MountingRailHelper,
    private terminalBlockManager: TerminalBlockManager,
    private articleMoveManager: ArticleMoveManager,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private replaceLabelingUtils: ReplaceLabelingUtils,
    private replaceBusbarHelper: ReplaceBusbarArticleHelper,
    private replaceBusbarService: ReplaceBusbarService,
    private insertArticleUtils: InsertArticleUtils,
    private replaceDynamicArticleService: ReplaceDynamicArticleService
  ) {
    super(
      userModel,
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      undoRedoHelper,
      alertHelper,
      compositeArticleUtils,
      containerService,
      labelingUtils,
      articleContainerUtils
    );
  }

  protected async proceedDelete(articleUsageIds: string[], deleteFunction: (articleUsageIdsToRemove: string[]) => Promise<SequenceOperation>): Promise<void> {
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();

    const terminalBlocks: TerminalBlock[] = this.terminalBlockManager.retrieveTerminalBlocksByUsageIds(
      articleUsageIds,
      [ArticleTypeIds.BLOCKING_ZONE, ArticleTypeIds.EMPTY_ZONE],
      insertHelper
    );

    let labelStripsToRecreate: ArticleContainer[] = this.replaceLabelingUtils.getLabelStripsToRecreateByUsageIds(articleUsageIds);
    ArrayUtils.mergeArrays(labelStripsToRecreate, this.getLabelStripsToRecreateByTerminalBlocks(terminalBlocks, articleUsageIds, insertHelper));
    labelStripsToRecreate = this.filterDynamicArticlesToRecreate(labelStripsToRecreate, articleUsageIds);

    let busbarsToRecreate: ArticleContainer[] = this.getBusbarsToRecreateByTerminalBlocks(terminalBlocks, articleUsageIds, insertHelper);
    busbarsToRecreate = this.filterDynamicArticlesToRecreate(busbarsToRecreate, articleUsageIds);

    const labelStripDetachOperation: UndoDetachArticlesOperation = await this.replaceLabelingHelper.detachLabelStrips(labelStripsToRecreate);
    await this.replaceBusbarHelper.detachBusbarsContainersUnchecked(busbarsToRecreate);
    const sequenceOperation: SequenceOperation = await deleteFunction(articleUsageIds);
    const undoDetachOperation: UndoDetachArticlesOperation = sequenceOperation.operations.find((operation) => operation instanceof UndoDetachArticlesOperation);
    if (undoDetachOperation) {
      const detachedUsageIds: string[] = undoDetachOperation.extractArticleUsageIds();

      for (let block of terminalBlocks) {
        block.remove(detachedUsageIds);
        const busbarsToRecreateInBlock: Busbar[] = this.getBusbarsToRecreateInBlock(block, insertHelper);
        const moveResult: MoveResult = await this.removeEmptySpaceInTerminalBlock(block, busbarsToRecreateInBlock, insertHelper);
        if (moveResult) {
          undoDetachOperation.mergeMoveMap(moveResult.moveMap);
          const busbarsChildrenMoveMap: Dictionary<string, Vector3> = this.replaceBusbarService.getChildrenMoveMap(busbarsToRecreateInBlock, moveResult.moveMap, insertHelper);
          this.insertArticleUtils.moveArticlesByMap(busbarsChildrenMoveMap);
          undoDetachOperation.mergeMoveMap(busbarsChildrenMoveMap);
        }
      }

      if (labelStripDetachOperation) {
        undoDetachOperation.merge(labelStripDetachOperation);
      }
      await this.undoRedoHelper.pushUndoOperation(sequenceOperation);
    }
  }

  private getBusbarsToRecreateInBlock(block: TerminalBlock, mountingRailInsertHelper: InsertArticleHelper): Busbar[] {
    const busbarsToShortenInBlock: Busbar[] = [];
    for (const busbar of this.replaceBusbarHelper.pendingBusbars) {
      const lastBusbarParentUsageId: string = this.replaceBusbarService.getLastBusbarParentUsageId(busbar, mountingRailInsertHelper);
      if (block.articleUsageIds.includes(lastBusbarParentUsageId)) {
        busbarsToShortenInBlock.push(busbar);
      }
    }
    return busbarsToShortenInBlock;
  }

  // exclude dynamic articles (e.g. label-strips, busbars) from re-creation which are explicitly selected for removal
  private filterDynamicArticlesToRecreate(dynamicArticles: ArticleContainer[], articleUsageIdsToRemove: string[]): ArticleContainer[] {
    return dynamicArticles
      .filter(article => articleUsageIdsToRemove.indexOf(article.articleGUID) < 0 && !ArticleUsageUtils.isCompositePartArticleUsage(article.articleUsage));
  }


  private removeEmptySpaceInTerminalBlock(terminalBlock: TerminalBlock, busbarsToRecreateInBlock: Busbar[], insertHelper: InsertArticleHelper): Promise<MoveResult> {
    if (terminalBlock.articleUsageIds.length < 2) {
      return;
    }

    const firstTerminal: ArticleContainer = this.containerService.findArticleContainerInGlobal(terminalBlock.articleUsageIds[0]);
    const lastTerminal: ArticleContainer = this.containerService.findArticleContainerInGlobal(
      terminalBlock.articleUsageIds[terminalBlock.articleUsageIds.length - 1]
    );
    if (firstTerminal && lastTerminal) {
      const maxAllowedMoveOffsetMap: Dictionary<string, number> = this.replaceBusbarService.getMaxAllowedMoveOffsetMap(busbarsToRecreateInBlock, insertHelper);
      return this.articleMoveManager.removeFreeSpaceBetweenArticles([firstTerminal, lastTerminal], false, false, maxAllowedMoveOffsetMap);
    }
    return null;
  }

  private getLabelStripsToRecreateByTerminalBlocks(
    terminalBlocks: TerminalBlock[],
    usageIdsToDelete: string[],
    insertHelper: InsertArticleHelper
  ): ArticleContainer[] {
    const labelStripsToDelete: ArticleContainer[] = [];
    for (const terminalBlock of terminalBlocks) {
      ArrayUtils.mergeArrays(labelStripsToDelete, this.getLabelStripsToRecreateByTerminalBlock(terminalBlock, usageIdsToDelete, insertHelper));
    }
    return labelStripsToDelete;
  }

  private getLabelStripsToRecreateByTerminalBlock(
    terminalBlock: TerminalBlock,
    usageIdsToDelete: string[],
    insertHelper: InsertArticleHelper
  ): ArticleContainer[] {
    // gaps caused by removed terminals from terminal block will be closed by moving terminals towards the beginning of the rail
    // so only label-strips which are connected on the right side from deleted terminals need to be re-created
    const startIndex: number = this.getLeftmostIndexInBlock(terminalBlock, usageIdsToDelete);
    if (startIndex < 0) {
      return [];
    }

    const labelStripsToDelete: ArticleContainer[] = [];
    const baseUsageIds: string[] = terminalBlock.articleUsageIds.slice(startIndex);
    const sourceLabelStrips: ArticleContainer[] = this.replaceLabelingUtils.getLabelStripsToRecreateByUsageIds(baseUsageIds);
    for (const sourceStrip of sourceLabelStrips) {
      const baseIds: string[] = this.replaceLabelingUtils.getAllBackwardUsageIdsByLabelStrip(sourceStrip, insertHelper.acceptingIdentity);
      if (
        !ArrayUtils.sourceArrayContainsAllElementsFromTargetArray(terminalBlock.articleUsageIds, baseIds) ||
        ArrayUtils.sourceArrayContainsElementFromTargetArray(baseIds, usageIdsToDelete)
      ) {
        // label-strip doesn't need to be re-created if it's connected only to the given terminal block
        // and no terminals (to which given strip is connected to) are supposed to be removed
        labelStripsToDelete.push(sourceStrip);
      }
    }
    return labelStripsToDelete;
  }


  private getBusbarsToRecreateByTerminalBlocks(terminalBlocks: TerminalBlock[], usageIdsToDelete: string[], insertHelper: InsertArticleHelper): ArticleContainer[] {
    const busbarsToRecreate: ArticleContainer[] = [];
    for (const terminalBlock of terminalBlocks) {
      ArrayUtils.mergeArrays(busbarsToRecreate, this.getBusbarsToRecreateByTerminalBlock(terminalBlock, usageIdsToDelete, insertHelper));
    }
    return busbarsToRecreate;
  }

  private getBusbarsToRecreateByTerminalBlock(terminalBlock: TerminalBlock, usageIdsToDelete: string[], insertHelper: InsertArticleHelper): ArticleContainer[] {
    const busbarsToRecreate: ArticleContainer[] = [];
    const busbarsOverArticlesToDelete: ArticleContainer[] = this.replaceBusbarService.getBusbarsToRecreateByUsageIds(usageIdsToDelete);
    for (const busbarOverArticle of busbarsOverArticlesToDelete) {
      const backwardUsageIds = this.replaceDynamicArticleService.getBackwardUsageIdsConnectedToAcceptingId(busbarOverArticle, insertHelper.acceptingIdentity);
      const backwardArticles: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(backwardUsageIds);
      const sortedBackwardArticles: ArticleContainer[] = insertHelper.sortArticles(backwardArticles);
      if (ArrayUtils.isNotEmpty(sortedBackwardArticles) && terminalBlock.containsArticle(sortedBackwardArticles[sortedBackwardArticles.length - 1].articleGUID)) {
        ArrayUtils.mergeArrays(busbarsToRecreate, [busbarOverArticle]);
      }
    }
    return busbarsToRecreate;
  }

  private getLeftmostIndexInBlock(terminalBlock: TerminalBlock, usageIds: string[]): number {
    let index: number = -1;
    for (const usageId of usageIds) {
      const usageIndex: number = terminalBlock.articleUsageIds.indexOf(usageId);
      if (index < 0 || (usageIndex >= 0 && usageIndex < index)) {
        index = usageIndex;
      }
    }
    return index;
  }

  public isResponsible(projectType: ProjectType): boolean {
    return IsProjectType.among(projectType,
      "MOUNTING_RAIL",
      "FREE_MOUNTING",
      "CIRCUIT_BOARD",
      "SPEEDWAY");
  }
}
